import React, {CSSProperties, ReactNode} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {AnimatePresence, motion, MotionProps} from 'framer-motion'

import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'
import minus from '../../../utils/minus'
import {Checkbox} from '../Checkbox'
import {Radio} from '../Radio'
import {Tag} from '../Tag'
import {Text} from '../Text'

const BOX_SHADOW_WIDTH = 1

export type ControlType = 'radio' | 'checkbox'

interface Props {
  selected: boolean
  dataId?: string
  name: string
  onChange: () => void
  value?: string
  children?: ReactNode
  labelText?: string
  withControlButton?: boolean
  withBorder?: boolean
  disabled?: boolean
  className?: string
  variant: ControlType
  style?: CSSProperties
  prefix?: ReactNode
  title?: ReactNode
  isVertical?: boolean
  hasCheckmarkError?: boolean
  hideDefaultControlButton?: boolean
  motionWrapperProps?: Pick<
    MotionProps,
    'initial' | 'animate' | 'exit' | 'transition' | 'variants'
  > & {
    key?: string
  }
  expandableContent?: ReactNode
}

const activeStyles = `
  border-color: ${cssTheme.colors.selectionCard.selected.border};
  box-shadow: inset 0 0 0 ${BOX_SHADOW_WIDTH}px
    ${cssTheme.colors.selectionCard.selected.border};
  background-color: ${cssTheme.colors.selectionCard.selected.background};
`

const disabledStyles = `
  border: 1px solid transparent;
  background-color: ${cssTheme.colors.selectionCard.disabled.background};
  box-shadow: inset 0 0 0 ${BOX_SHADOW_WIDTH}px
      ${cssTheme.colors.transparent};
  color: ${cssTheme.colors.selectionCard.disabled.description};
  cursor: not-allowed;
  &.withBorder {
    border-color: ${cssTheme.colors.selectionCard.disabled.border};
    box-shadow: inset 0 0 0 ${BOX_SHADOW_WIDTH}px
      ${cssTheme.colors.selectionCard.disabled.border};
  }
`

const PrefixWrapper = styled.div`
  grid-area: prefix;
  margin-right: ${cssTheme.layout.spacing.s300};
  color: ${cssTheme.colors.selectionCard.default.icon};
`

const TitleWrapper = styled.div`
  grid-area: title;
  color: ${cssTheme.colors.selectionCard.hover.title};
`

const ContentContainer = styled.div`
  ${cssTheme.typography.bodyS}
  grid-area: content;
  color: ${cssTheme.colors.selectionCard.default.description};
`

const Wrapper = styled.label`
  width: 100%;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  text-align: left;
  margin: ${BOX_SHADOW_WIDTH}px; /* prevents the box-shadow from being cut off*/

  border-radius: ${cssTheme.layout.radius.md};
  cursor: pointer;
  transition-property: background-color, border-color, box-shadow;
  transition-duration: 0.3s;
  transition-timing-function: cubic-bezier(0.33, 1, 0.68, 1);

  border: 1px solid ${cssTheme.colors.transparent};
  background-color: ${cssTheme.colors.selectionCard.default.background};
  color: ${cssTheme.colors.selectionCard.default.description};
  &.withBorder {
    border-color: ${cssTheme.colors.selectionCard.default.border};
  }

  &.isSelected {
    ${activeStyles}
  }
  &.isDisabled,
  &.isSelected.isDisabled {
    ${disabledStyles}
  }
  ${linariaMq.desktopXs} {
    &:hover {
      background-color: ${cssTheme.colors.selectionCard.hover.background};
      &.withBorder {
        border-color: ${cssTheme.colors.selectionCard.hover.border};
        box-shadow: inset 0 0 0 ${BOX_SHADOW_WIDTH}px
          ${cssTheme.colors.selectionCard.hover.border};
      }
      &.isSelected:not(.isDisabled) {
        ${activeStyles}
      }
      &.isDisabled {
        ${disabledStyles}
      }
    }
    &:active:not(.isDisabled),
    &:focus:not(.isDisabled),
    &:has(input:focus) {
      &.isSelected {
        ${activeStyles}
      }
    }
  }

  &.isDisabled {
    ${PrefixWrapper} {
      color: ${cssTheme.colors.selectionCard.disabled.icon};
      img {
        opacity: 0.2;
      }
    }
    ${TitleWrapper} {
      color: ${cssTheme.colors.selectionCard.disabled.title};
    }
    ${ContentContainer} {
      color: ${cssTheme.colors.selectionCard.disabled.description};
    }
  }
`

const ControlButtonWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  grid-area: control;
  margin-left: ${cssTheme.layout.spacing.s300};

  &.--hide-default-control-button {
    opacity: 0;
  }
`

const SelectionCardBody = styled.div`
  width: 100%;
  padding: ${cssTheme.layout.spacing.s500};
  padding-right: ${cssTheme.layout.spacing.s400};
  flex-grow: 1;
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-areas:
    'prefix title'
    'prefix content';
  &.--with-control-button {
    grid-template-columns: auto 1fr auto;
    grid-template-areas:
      'prefix title control'
      'prefix content control';
  }
  &.--vertical {
    grid-template-columns: 1fr auto;
    grid-template-areas:
      'prefix control'
      'title control'
      'content control';
  }
`

const LabelText = styled(Tag)`
  position: absolute;
  top: ${minus(cssTheme.layout.spacing.s400)};
  left: ${cssTheme.layout.spacing.s300};
  &.isSelected {
    background-color: ${cssTheme.colors.selectionCard.selected.tag.background};
    color: ${cssTheme.colors.selectionCard.selected.tag.content};
  }
`

const MotionWrapper = styled(motion.div)`
  width: calc(100% - 2px);
  margin: 1px;
  padding: ${cssTheme.layout.spacing.s600};
  background-color: ${cssTheme.colors.selectionCard.default.background};
  border-bottom-left-radius: ${cssTheme.layout.radius.md};
  border-bottom-right-radius: ${cssTheme.layout.radius.md};
`

export const SelectionCard = ({
  selected,
  dataId = 'SelectionCard',
  name,
  onChange,
  value,
  children,
  labelText,
  withControlButton = true,
  withBorder = true,
  disabled = false,
  className,
  variant = 'radio',
  style,
  prefix,
  title,
  isVertical = false,
  hasCheckmarkError,
  motionWrapperProps,
  expandableContent,
  hideDefaultControlButton = false
}: Props) => {
  return (
    <Wrapper
      className={cx(
        withBorder && 'withBorder',
        selected && 'isSelected',
        disabled && 'isDisabled',
        className
      )}
      onClick={withControlButton ? undefined : onChange}
      data-id={dataId}
      style={style}
    >
      {labelText && (
        <LabelText
          variant={disabled ? 'neutral' : 'info'}
          className={cx(
            selected && !disabled && 'isSelected',
            'tagLabelContainer'
          )}
        >
          <Text variant="labelXS">{labelText}</Text>
        </LabelText>
      )}

      <SelectionCardBody
        className={cx(
          isVertical && `--vertical`,
          withControlButton && `--with-control-button`
        )}
      >
        {Boolean(prefix) && <PrefixWrapper>{prefix}</PrefixWrapper>}
        {Boolean(title) && (
          <TitleWrapper>
            <Text variant="titleS">{title}</Text>
          </TitleWrapper>
        )}
        {Boolean(children) && <ContentContainer>{children}</ContentContainer>}
        {withControlButton && (
          <ControlButtonWrapper
            className={cx(
              !selected &&
                hideDefaultControlButton &&
                '--hide-default-control-button'
            )}
          >
            {variant === 'checkbox' ? (
              <Checkbox
                checked={selected}
                onChange={onChange}
                disabled={disabled}
                hasError={hasCheckmarkError}
                name={name}
                value={value}
              />
            ) : (
              <Radio
                checked={selected}
                onChange={onChange}
                disabled={disabled}
                name={name}
                value={value}
              />
            )}
          </ControlButtonWrapper>
        )}
      </SelectionCardBody>

      <AnimatePresence initial={false}>
        {selected && expandableContent && !disabled && (
          <MotionWrapper {...motionWrapperProps}>
            {expandableContent}
          </MotionWrapper>
        )}
      </AnimatePresence>
    </Wrapper>
  )
}
